/**
 * Small behaviours for the Sofario theme: banner slides, home video, colour dots on
 * product cards, the colour choice on older sofas, cookie settings links and back to top.
 * Everything here is an enhancement. If this file fails to load, pages still work.
 */
(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Icons are SVG, which has no `hidden` property, so the attribute itself is set. */
  const setShown = (element, shown) => element?.toggleAttribute('hidden', !shown);

  /** On wide screens the page scrolls inside a wrapper, not the window. */
  const getScrollContainer = () =>
    (window.matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')) ||
    document.scrollingElement ||
    document.documentElement;

  class SofarioHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.lines = [...this.querySelectorAll('[data-go]')];
      this.toggleButton = this.querySelector('[data-toggle]');
      this.index = 0;
      if (this.slides.length < 2) return;

      this.style.setProperty('--sofario-hero-interval', `${Number(this.dataset.interval) || 6000}ms`);
      this.userPaused = this.dataset.autoplay !== 'true';
      this.autoplayAllowed = !prefersReducedMotion();
      this.hovering = false;

      this.addEventListener('click', this.#handleClick);
      // The filling line drives the timing, so a pause holds the line and the slide together.
      this.addEventListener('animationend', this.#handleFillEnd);
      this.addEventListener('pointerenter', this.#handleHoverStart);
      this.addEventListener('pointerleave', this.#handleHoverEnd);
      this.addEventListener('focusin', this.#handleHoverStart);
      this.addEventListener('focusout', this.#handleHoverEnd);
      this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.#syncState);

      this.#syncState();
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.#syncState);
    }

    show(next) {
      this.index = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
      this.lines.forEach((line, position) => {
        line.setAttribute('aria-current', String(position === this.index));
      });
    }

    #handleClick = (event) => {
      const line = event.target.closest('[data-go]');
      if (line) {
        this.show(Number(line.dataset.go));
        return;
      }
      const step = event.target.closest('[data-step]');
      if (step) {
        this.show(this.index + Number(step.dataset.step));
        return;
      }
      if (event.target.closest('[data-toggle]')) {
        this.userPaused = !this.userPaused;
        this.#syncState();
      }
    };

    #handleFillEnd = (event) => {
      if (event.target.matches('[data-fill]')) this.show(this.index + 1);
    };

    #handleHoverStart = () => {
      this.hovering = true;
      this.#syncState();
    };

    #handleHoverEnd = () => {
      this.hovering = false;
      this.#syncState();
    };

    #handleTouchStart = (event) => {
      this.touchStartX = event.changedTouches[0].clientX;
    };

    #handleTouchEnd = (event) => {
      const distance = event.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(distance) > 50) this.show(this.index + (distance < 0 ? 1 : -1));
    };

    #syncState = () => {
      const running = this.autoplayAllowed && !this.userPaused;
      this.toggleAttribute('data-running', running);
      this.toggleAttribute('data-paused', running && (this.hovering || document.hidden));

      if (!this.toggleButton) return;
      setShown(this.toggleButton.querySelector('[data-pause]'), running);
      setShown(this.toggleButton.querySelector('[data-play]'), !running);
      this.toggleButton.setAttribute('aria-label', running ? 'Pause slides' : 'Play slides');
    };
  }

  class SofarioVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.buttons = this.querySelector('.sofario-video__buttons');
      if (!this.video) return;

      // Our own buttons replace the browser's controls, which stay as the fallback without this script.
      this.video.removeAttribute('controls');
      this.buttons.hidden = false;
      this.userPaused = prefersReducedMotion();

      this.addEventListener('click', this.#handleClick);
      this.video.addEventListener('waiting', this.#handleWaiting);
      this.video.addEventListener('playing', this.#handlePlaying);
      this.video.addEventListener('pause', this.#syncButtons);
      this.video.addEventListener('play', this.#syncButtons);

      this.observer = new IntersectionObserver(this.#handleVisibility, { threshold: 0.35 });
      this.observer.observe(this);
      this.#syncButtons();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async #play() {
      this.toggleAttribute('data-waiting', this.video.readyState < 3);
      try {
        await this.video.play();
      } catch {
        this.toggleAttribute('data-waiting', false);
      }
    }

    #handleVisibility = ([entry]) => {
      if (entry.isIntersecting && !this.userPaused) {
        this.#play();
      } else if (!entry.isIntersecting) {
        this.video.pause();
      }
    };

    #handleClick = (event) => {
      if (event.target.closest('[data-sound-toggle]')) {
        this.video.muted = !this.video.muted;
        if (!this.video.muted && this.video.paused) {
          this.userPaused = false;
          this.#play();
        }
        this.#syncButtons();
        return;
      }
      if (event.target.closest('[data-play-toggle]') || event.target === this.video) {
        this.userPaused = !this.video.paused;
        if (this.userPaused) {
          this.video.pause();
        } else {
          this.#play();
        }
      }
    };

    #handleWaiting = () => this.toggleAttribute('data-waiting', true);

    #handlePlaying = () => this.toggleAttribute('data-waiting', false);

    #syncButtons = () => {
      const playButton = this.querySelector('[data-play-toggle]');
      const soundButton = this.querySelector('[data-sound-toggle]');
      const { paused, muted } = this.video;
      setShown(playButton.querySelector('[data-pause]'), !paused);
      setShown(playButton.querySelector('[data-play]'), paused);
      playButton.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      setShown(soundButton.querySelector('[data-sound-off]'), muted);
      setShown(soundButton.querySelector('[data-sound-on]'), !muted);
      soundButton.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
    };
  }

  if (!customElements.get('sofario-hero')) customElements.define('sofario-hero', SofarioHero);
  if (!customElements.get('sofario-video')) customElements.define('sofario-video', SofarioVideo);

  /**
   * Sofa video gallery: videos play without sound while on screen, the arrows and the bar under the
   * row follow the scroll, and tapping a video opens it large with sound.
   */
  class SofarioVideoGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.items = [...this.querySelectorAll('.sofario-vg__item')];
      this.dialog = this.querySelector('[data-dialog]');
      this.dialogVideo = this.querySelector('[data-dialog-video]');
      this.dialogCaption = this.querySelector('[data-dialog-caption]');
      this.progress = this.querySelector('[data-progress]');
      this.arrows = [...this.querySelectorAll('[data-step]')];
      if (!this.track) return;

      try {
        this.sources = JSON.parse(this.querySelector('[data-sources]')?.textContent || '[]');
      } catch {
        this.sources = [];
      }

      for (const item of this.items) {
        const video = item.querySelector('video');
        if (!video) continue;
        video.addEventListener('playing', () => item.classList.add('is-playing'));
        video.addEventListener('pause', () => item.classList.remove('is-playing'));
      }

      if (this.hasAttribute('data-autoplay') && !prefersReducedMotion()) {
        this.observer = new IntersectionObserver(this.#handleVisibility, { threshold: 0.6 });
        this.items.forEach((item) => this.observer.observe(item));
      }

      this.addEventListener('click', this.#handleClick);
      this.track.addEventListener('scroll', this.#handleScroll, { passive: true });
      this.dialog?.addEventListener('close', this.#handleDialogClose);
      this.dialog?.addEventListener('click', this.#handleBackdropClick);
      this.resizeObserver = new ResizeObserver(this.#handleScroll);
      this.resizeObserver.observe(this.track);
      this.#handleScroll();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    #handleVisibility = (entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');
        if (!video) continue;
        if (entry.isIntersecting && !this.dialog?.open) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    };

    #handleClick = (event) => {
      const step = event.target.closest('[data-step]');
      if (step) {
        const item = this.items[0];
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const itemWidth = (item?.getBoundingClientRect().width ?? 300) + gap;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / itemWidth) - 1) || 1;
        this.track.scrollBy({
          left: Number(step.dataset.step) * itemWidth * perPage,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        return;
      }

      const open = event.target.closest('[data-open]');
      if (open) this.#open(Number(open.dataset.open));

      if (event.target.closest('[data-close]')) this.dialog?.close();
    };

    #handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const [previous, next] = this.arrows;
      if (previous) previous.disabled = scrollLeft <= 4;
      if (next) next.disabled = scrollLeft >= maxScroll - 4;
      // When every video already fits, the arrows have nothing to do
      this.querySelector('[data-arrows]')?.toggleAttribute('hidden', maxScroll <= 4);

      if (this.progress) {
        const visible = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
        this.progress.style.setProperty('--sofario-vg-visible', `${visible * 100}%`);
        this.progress.style.setProperty('--sofario-vg-offset', `${clientWidth > 0 ? (scrollLeft / clientWidth) * 100 : 0}%`);
        this.progress.parentElement?.toggleAttribute('hidden', visible >= 1);
      }
    };

    /** Opens one video large, with sound, and pauses the row behind it. */
    #open(index) {
      const source = this.sources[index];
      if (!this.dialog || !this.dialogVideo || !source?.src) return;

      this.items.forEach((item) => item.querySelector('video')?.pause());
      this.dialogVideo.poster = source.poster || '';
      this.dialogVideo.src = source.src;
      this.dialogVideo.muted = false;

      const caption = this.items[index]?.querySelector('.sofario-vg__caption');
      if (this.dialogCaption) this.dialogCaption.innerHTML = caption ? caption.innerHTML : '';

      this.dialog.showModal();
      this.dialogVideo.play().catch(() => {});
    }

    #handleBackdropClick = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };

    #handleDialogClose = () => {
      if (!this.dialogVideo) return;
      this.dialogVideo.pause();
      this.dialogVideo.removeAttribute('src');
      this.dialogVideo.load();
      // Videos still on screen carry on playing
      this.observer?.takeRecords();
      if (this.observer) {
        this.items.forEach((item) => {
          this.observer.unobserve(item);
          this.observer.observe(item);
        });
      }
    };
  }

  if (!customElements.get('sofario-video-gallery')) customElements.define('sofario-video-gallery', SofarioVideoGallery);

  const PRODUCT_AREA = '[data-product-grid-content], #quick-add-modal-content, .quick-add-modal__content';

  /**
   * "Complete the set" on a sofa page: tick boxes for the sofa's own footstool and coffee table, which
   * follow the chosen colour, or a separate footstool linked in its "Matching stool" field.
   * Ticked items are added just before the sofa (see initExtras).
   */
  class SofarioExtras extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
      } catch {
        this.variants = [];
      }
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.area = this.closest(PRODUCT_AREA) || this.closest('product-form-component');
      this.onChange = (event) => {
        if (event.target.closest?.('variant-picker')) this.update();
      };
      this.area?.addEventListener('change', this.onChange);
      this.update();
    }

    disconnectedCallback() {
      this.area?.removeEventListener('change', this.onChange);
    }

    /** The chosen value of each option, in order, read straight from the size and colour choices. */
    chosenOptions() {
      const options = this.area?.querySelectorAll('variant-picker .variant-option:not(.sofario-colour-choice)') ?? [];
      return [...options].map((option) => option.querySelector('input:checked, select')?.value);
    }

    update() {
      const chosen = this.chosenOptions();
      const chosenSize = (chosen[this.sizeIndex] || '').toLowerCase();
      // A sofa that only has a linked footstool has no Footstool size to check.
      const isExtra =
        this.sizeIndex >= 0 && (!chosenSize || chosenSize.includes('stool') || chosenSize.includes('table'));
      let anyShown = false;

      for (const row of this.querySelectorAll('.sofario-extra')) {
        const match = this.variants.find(
          (variant) =>
            variant.kind === row.dataset.kind &&
            variant.available &&
            (variant.linked ||
              variant.options.every((value, index) => index === this.sizeIndex || value === chosen[index]))
        );
        const shown = !isExtra && !!match;
        row.hidden = !shown;
        anyShown = anyShown || shown;

        // A ticked box stays ticked when the colour changes, and follows the new colour.
        const check = row.querySelector('input');
        if (!shown) {
          check.checked = false;
          continue;
        }
        check.value = match.id;
        row.querySelector('.sofario-extra__now').textContent = `+ ${match.price}`;
        row.querySelector('.sofario-extra__was').textContent = match.was;
        const photo = row.querySelector('.sofario-extra__photo');
        if (match.photo) photo.src = match.photo;
        photo.hidden = !match.photo;
      }
      this.hidden = !anyShown;
    }

    /** Variant ids of the ticked boxes that are showing. */
    tickedIds() {
      if (this.hidden) return [];
      return [...this.querySelectorAll('.sofario-extra:not([hidden]) input:checked')].map((check) => Number(check.value));
    }

    clearTicks() {
      for (const check of this.querySelectorAll('input:checked')) check.checked = false;
    }
  }

  if (!customElements.get('sofario-extras')) customElements.define('sofario-extras', SofarioExtras);

  /** Hovering a colour dot on a product card fades in that colour's photo. */
  function initCardColourPreview() {
    if (!window.matchMedia('(hover: hover)').matches) return;

    document.addEventListener('pointerover', (event) => {
      const dot = event.target.closest?.('.sofario-colours .sofario-dot[data-image]');
      if (!dot) return;

      const gallery = dot.closest('product-card')?.querySelector('.card-gallery');
      if (!gallery) return;

      let preview = gallery.querySelector('.sofario-preview');
      if (!preview) {
        preview = new Image();
        preview.className = 'sofario-preview';
        preview.alt = '';
        gallery.append(preview);
      }
      preview.onload = () => preview.classList.add('is-visible');
      preview.src = dot.dataset.image;
    });

    document.addEventListener('pointerout', (event) => {
      const dot = event.target.closest?.('.sofario-colours .sofario-dot[data-image]');
      if (!dot) return;
      dot.closest('product-card')?.querySelector('.sofario-preview')?.classList.remove('is-visible');
    });
  }

  /** Keeps the dots under a product card's photos in step with the photo showing. */
  function initCardDots() {
    document.addEventListener('slideshow:select', (event) => {
      const dots = event.target.closest?.('.card-gallery')?.querySelector('.sofario-card-dots');
      if (!dots) return;
      const index = event.detail?.index ?? 0;
      [...dots.children].forEach((dot, position) => dot.classList.toggle('is-active', position === index));
    });
  }

  /**
   * Moves the photo gallery next to `source` (a product page or quick view) to the photo with `mediaId`.
   * Returns false when that photo is not in the gallery.
   */
  function showGalleryPhoto(source, mediaId) {
    if (!mediaId) return false;
    const productArea = source.closest('[data-product-grid-content], #quick-add-modal-content, .quick-add-modal__content');
    const slideshow = productArea?.querySelector('media-gallery slideshow-component[ref="slideshow"]');
    const slides = slideshow?.slides ?? [];
    const index = slides.findIndex(
      (slide) => slide.getAttribute('slide-id') === mediaId || slide.querySelector(`[data-media-id="${mediaId}"]`)
    );
    if (index < 0) return false;

    // The photo may not have loaded yet, so it is fetched straight away rather than when it scrolls into view.
    const image = slides[index].querySelector('img');
    if (image) image.loading = 'eager';
    // A long jump is made at once, so a chosen colour shows straight away rather than after a scroll.
    const nearby = Math.abs(index - slideshow.current) <= 1;
    if (index !== slideshow.current) slideshow.select(index, undefined, { animate: nearby && !prefersReducedMotion() });
    return true;
  }

  /**
   * The photo to show for the choices ticked in a product area.
   * Older sofas keep their colours in photos, so between sofa sizes the ticked colour stays on screen.
   * An arm chair, coffee table or footstool shows its own photo, or the sofa photo when it has none.
   */
  function photoForChoices(productArea, variantMediaId) {
    const colourMediaId = productArea.querySelector('.sofario-colour-choice input:checked')?.dataset.mediaId;
    if (!productArea.querySelector('variant-picker input[data-sofario-extra]:checked')) {
      return colourMediaId || variantMediaId;
    }
    if (variantMediaId) return variantMediaId;
    const sofaSize = productArea.querySelector(
      'variant-picker .variant-option--sizes input[data-sofario-media-id]:not([data-sofario-extra])'
    );
    return colourMediaId || sofaSize?.dataset.sofarioMediaId;
  }

  /**
   * Picking a size or colour shows its photo at once. The theme then loads the new price and stock
   * in the background, which used to hold the photo back by a second or more.
   * Pointing at a choice starts loading its photo, so it is ready by the time it is clicked.
   */
  function initInstantVariantPhoto() {
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.closest('variant-picker') || input.closest('product-card')) return;
      const productArea = input.closest('[data-product-grid-content], #quick-add-modal-content, .quick-add-modal__content');
      if (!productArea) return;
      showGalleryPhoto(input, photoForChoices(productArea, input.dataset.sofarioMediaId));
    });

    const warm = (event) => {
      const input = event.target.closest?.('label')?.querySelector('input[data-sofario-media-id], input[data-media-id]');
      if (!input || input.dataset.sofarioWarm || input.closest('product-card')) return;
      input.dataset.sofarioWarm = 'true';
      const mediaId = input.dataset.sofarioMediaId || input.dataset.mediaId;
      const productArea = input.closest('[data-product-grid-content], #quick-add-modal-content, .quick-add-modal__content');
      const image = productArea?.querySelector(`media-gallery slideshow-slide[slide-id="${mediaId}"] img`);
      if (image) image.loading = 'eager';
    };
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('focusin', warm);
  }

  /**
   * Photos past the first few load only when needed. Moving through a gallery or a card loads the
   * next photos one step ahead, and pointing at a card loads its second photo, so the arrows feel instant.
   */
  function initPhotoLoadAhead() {
    const loadSlides = (slideshow, index, offsets) => {
      const slides = slideshow?.slides ?? [];
      for (const offset of offsets) {
        const image = slides[index + offset]?.querySelector('img[loading="lazy"]');
        if (image) image.loading = 'eager';
      }
    };

    document.addEventListener('slideshow:select', (event) => {
      const slideshow = event.target;
      if (!(slideshow instanceof HTMLElement) || !slideshow.closest('media-gallery, .card-gallery')) return;
      loadSlides(slideshow, event.detail?.index ?? 0, [-1, 1, 2]);
    });

    document.addEventListener(
      'pointerover',
      (event) => {
        const gallery = event.target.closest?.('.card-gallery');
        if (!gallery || gallery.dataset.sofarioWarm) return;
        gallery.dataset.sofarioWarm = 'true';
        loadSlides(gallery.querySelector('slideshow-component'), 0, [1, -1]);
      },
      { passive: true }
    );
  }

  /**
   * Older sofas keep their colours in photos, not in variants. Picking one shows its photo,
   * and the choice is added to the order line when the shopper adds to cart.
   */
  function initColourChoice() {
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      const choice = input.closest('.sofario-colour-choice');
      if (!choice) return;

      const chosenName = choice.querySelector('.variant-option__swatch-value');
      if (chosenName) chosenName.textContent = input.value;

      showGalleryPhoto(input, input.dataset.mediaId);
    });

    document.addEventListener('formdata', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

      const productArea = form.closest('[data-product-grid-content], #quick-add-modal-content');
      const chosen = productArea?.querySelector('.sofario-colour-choice input:checked');
      if (chosen instanceof HTMLInputElement) event.formData.set('properties[Colour]', chosen.value);
    });
  }

  /**
   * Add to cart with "Complete the set" ticked: the footstool or coffee table goes into the basket first,
   * then the theme adds the sofa as usual, so the basket shows both. This listens on the window, ahead of
   * the theme's own listener on the document. If adding the extras fails, the sofa is still added.
   */
  function initExtras() {
    window.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
        if (form.dataset.sofarioExtrasSent) {
          delete form.dataset.sofarioExtrasSent;
          return;
        }

        const area = form.closest(PRODUCT_AREA) || form.closest('product-form-component');
        const extras = form.querySelector('sofario-extras');
        const ids = extras?.tickedIds?.() ?? [];
        if (!ids.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        // Older sofas keep the colour on the order line, so the extras carry it too.
        const colour = area?.querySelector('.sofario-colour-choice input:checked')?.value;
        const properties = colour ? { Colour: colour } : {};
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1, properties })) }),
          });
          if (response.ok) extras.clearTicks();
        } catch {
          // The sofa is still added below.
        }

        form.dataset.sofarioExtrasSent = 'true';
        form.requestSubmit();
      },
      { capture: true }
    );
  }

  /** Any link ending in #cookie-settings reopens the cookie choices. Without the banner script it opens the page it points at. */
  function initCookieSettingsLinks() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href$="#cookie-settings"]');
      if (!link || typeof window.privacyBanner?.showPreferences !== 'function') return;
      event.preventDefault();
      window.privacyBanner.showPreferences();
    });
  }

  /** A size further along the row, for example opened from a link, is slid into view when the page opens. */
  function initSizeRow() {
    for (const row of document.querySelectorAll('.sofario-sizes')) {
      const chosen = row.querySelector('input:checked')?.closest('label');
      if (!chosen) continue;
      const rowBox = row.getBoundingClientRect();
      const chosenBox = chosen.getBoundingClientRect();
      if (chosenBox.right > rowBox.right) row.scrollLeft += chosenBox.left - rowBox.left - 3;
    }
  }

  /**
   * The call and WhatsApp buttons in the phone menu use the links from the top bar, so the numbers
   * are only set in one place. The theme can redraw the menu, so they are filled again when it opens.
   */
  function initMenuContactButtons() {
    const fill = () => {
      for (const button of document.querySelectorAll('a[data-sofario-contact]')) {
        const source = document.querySelector(
          button.dataset.sofarioContact === 'tel' ? '.sofario-topbar a[href^="tel:"]' : '.sofario-topbar a[href*="wa.me"]'
        );
        if (!source) continue;
        button.href = source.href;
        const icon = source.querySelector('svg');
        if (icon && !button.querySelector('svg')) button.prepend(icon.cloneNode(true));
        button.hidden = false;
      }
    };
    fill();
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('.header__icon--menu')) fill();
    });
  }

  /** Long lists get a way back to the top. */
  function initBackToTop() {
    const template = document.querySelector('main[data-template]')?.dataset.template ?? '';
    if (!/^(index|collection|search)/.test(template)) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sofario-top';
    button.setAttribute('aria-label', 'Back to top');
    button.innerHTML =
      '<svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M10 16V4M5 9l5-5 5 5"/></svg>';
    button.addEventListener('click', () => {
      getScrollContainer().scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    });
    document.body.append(button);

    let ticking = false;
    const update = () => {
      button.classList.toggle('is-visible', getScrollContainer().scrollTop > 1200);
      ticking = false;
    };
    // Capture, because the page can scroll inside a wrapper and scroll events do not bubble.
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true, capture: true }
    );
  }

  initCardColourPreview();
  initCardDots();
  initColourChoice();
  initExtras();
  initInstantVariantPhoto();
  initPhotoLoadAhead();
  initCookieSettingsLinks();
  initMenuContactButtons();
  initSizeRow();
  initBackToTop();
})();
